import { useCallback, useEffect, useRef, useState } from 'react';
import type { HomeSummary, Me } from '../../services/src/shared/api-types.ts';
import type { Api } from './api.ts';
import type { Auth } from './auth.ts';
import { num, shortStamp } from './format.ts';
import { onPlaidReturnPath } from './plaid.ts';
import { Activity } from './screens/Activity.tsx';
import { ConnectionsScreen } from './screens/Connections.tsx';
import { Home } from './screens/Home.tsx';
import { Review } from './screens/Review.tsx';
import { Unmatched } from './screens/Unmatched.tsx';
import { BootScreen } from './ui.tsx';

export type Screen = 'home' | 'review' | 'unmatched' | 'connections' | 'activity';

const SCREENS: { id: Screen; label: string; adminOnly?: boolean }[] = [
  { id: 'home', label: 'Home' },
  { id: 'review', label: 'Review' },
  { id: 'unmatched', label: 'Unmatched' },
  { id: 'connections', label: 'Connections', adminOnly: true },
  { id: 'activity', label: 'Activity' },
];

function screenFromHash(): Screen {
  // A bank's OAuth sign-in comes back to /plaid-return; Connections picks Link up again.
  if (onPlaidReturnPath() && !location.hash) return 'connections';
  const id = location.hash.replace(/^#\/?/, '').split('/')[0] as Screen;
  return SCREENS.some((s) => s.id === id) ? id : 'home';
}

/** What every screen gets from the shell. */
export interface ScreenProps {
  api: Api;
  summary: HomeSummary | null;
  summaryError: string | null;
  /** Re-reads the counts in the sidebar and on Home (after a decision or a new login). */
  refreshSummary: () => void;
  isAdmin: boolean;
  /** Mock mode (npm run dev -w web): Plaid Link is simulated in the page. */
  mock: boolean;
}

export function App({ api, auth, mock }: { api: Api; auth: Auth; mock: boolean }) {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>(screenFromHash);
  const [summary, setSummary] = useState<HomeSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    api.me().then(setMe, (e: Error) => setError(e.message));
    const onHash = () => setScreen(screenFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [api]);

  const isAdmin = Boolean(me?.groups.includes('admin'));
  const canReview = isAdmin || Boolean(me?.groups.includes('reviewer'));

  const refreshSummary = useCallback(() => {
    api.home().then(
      (s) => {
        setSummary(s);
        setSummaryError(null);
      },
      (e: Error) => setSummaryError(e.message),
    );
  }, [api]);

  useEffect(() => {
    if (canReview) refreshSummary();
  }, [canReview, refreshSummary]);

  const visible = SCREENS.filter((s) => !s.adminOnly || isAdmin);
  const current = visible.some((s) => s.id === screen) ? screen : 'home';
  const label = visible.find((s) => s.id === current)?.label ?? 'Home';

  // On navigation, move focus to the content so keyboard and screen reader users land on the new screen.
  useEffect(() => {
    document.title = `${label} · Four Winds Sync`;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    mainRef.current?.focus();
  }, [current, label]);

  if (error) return <p className="fatal" role="alert">{error}</p>;
  if (!me) return <BootScreen />;

  const reviewCount = summary?.review.count;
  const unmatchedCount = summary ? summary.unmatched.lines.count + summary.unmatched.payments.count : undefined;
  const attention = summary?.connections.totals.attentionLogins ?? 0;
  const lastSync = summary?.lastSync;

  const props: ScreenProps = { api, summary, summaryError, refreshSummary, isAdmin, mock };

  return (
    <div className="shell">
      <a className="skip" href="#content" onClick={(e) => (e.preventDefault(), mainRef.current?.focus())}>
        Skip to content
      </a>
      <nav aria-label="Main" className="side">
        <div className="brand">
          <strong>Four Winds</strong>
          <span>Payment matching</span>
        </div>
        <div className="side-nav">
          {visible.map((s) => (
            <a key={s.id} href={`#/${s.id}`} aria-current={s.id === current ? 'page' : undefined}>
              {s.label}
              {s.id === 'review' && reviewCount !== undefined && (
                <span className="nav-count">
                  {num(reviewCount)}
                  <span className="visually-hidden"> waiting</span>
                </span>
              )}
              {s.id === 'unmatched' && unmatchedCount !== undefined && (
                <span className="nav-count">
                  {num(unmatchedCount)}
                  <span className="visually-hidden"> items</span>
                </span>
              )}
              {s.id === 'connections' && attention > 0 && (
                <span className="nav-badge">
                  {attention}
                  <span className="visually-hidden"> {attention === 1 ? 'login needs' : 'logins need'} attention</span>
                </span>
              )}
            </a>
          ))}
        </div>
        <div className="side-foot">
          {mock && <span className="mock-flag">Mock data</span>}
          {lastSync && <span>{lastSync.ok ? `Last sync ${shortStamp(lastSync.at)}` : `Last sync failed ${shortStamp(lastSync.at)}`}</span>}
          {summary && !lastSync && <span>No sync yet</span>}
          <span className="email">{me.email ?? auth.profile().username ?? 'Signed in'}</span>
          <button type="button" onClick={() => auth.signOut()}>
            Sign out
          </button>
        </div>
      </nav>
      <main className="main" id="content" ref={mainRef} tabIndex={-1}>
        {!canReview ? (
          <div className="page">
            <h1 className="page-title">Four Winds Sync</h1>
            <p className="state">Your account isn't in a group yet. Ask an admin to add you to reviewer or admin.</p>
          </div>
        ) : current === 'home' ? (
          <Home {...props} />
        ) : current === 'review' ? (
          <Review {...props} />
        ) : current === 'unmatched' ? (
          <Unmatched {...props} />
        ) : current === 'connections' ? (
          <ConnectionsScreen {...props} />
        ) : (
          <Activity {...props} />
        )}
      </main>
    </div>
  );
}
